import React, { useState, useEffect } from 'react';
import '../../styles/AdminDashboard.scss';
import { cmsAPI, getImageUrl } from '../../utils/api';
import { FaImages, FaCloudUploadAlt, FaTrash, FaTimes } from 'react-icons/fa';

const AdminGallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ title: '', image: null });
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchGallery = async () => {
    try {
      const res = await cmsAPI.getGallery();
      if (res.data.success) setItems(res.data.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGallery();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setFormData({ ...formData, image: null });
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image) {
      alert('Please select an image');
      return;
    }

    setIsSubmitting(true);
    const data = new FormData();
    data.append('title', formData.title);
    data.append('image', formData.image);

    try {
      const res = await cmsAPI.addGalleryItem(data);
      if (res.data.success) {
        fetchGallery();
        setFormData({ title: '', image: null });
        setImagePreview(null);
        e.target.reset();
      }
    } catch (error) {
      console.error('Error adding gallery item:', error);
      alert('Failed to add image');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;
    
    try {
      const res = await cmsAPI.deleteGalleryItem(id);
      if (res.data.success) {
        fetchGallery();
      }
    } catch (error) {
      console.error('Error deleting gallery item:', error);
    }
  };

  return (
    <div className="admin-dashboard-page">
      <div className="dashboard-header">
        <h1>CMS - Gallery</h1>
        <p>Upload and manage images for your website's public gallery</p>
      </div>

      <div className="admin-card">
        <h3><FaImages /> Add New Image</h3>
        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-group">
            <label>Image Title (Optional)</label>
            <input 
              type="text" placeholder="e.g. Office Event 2024"
              value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})}
            />
          </div>

          <div className="form-group">
            <label>Select Photo</label>
            <div className="file-input-wrapper">
              <FaCloudUploadAlt size={30} color="#4318ff" />
              <p style={{ margin: '10px 0 0 0', color: '#a3aed0', fontSize: '14px' }}>
                {formData.image ? formData.image.name : 'Click to upload or drag image here'}
              </p>
              <input type="file" accept="image/*" required onChange={handleImageChange} />
              
              {imagePreview && (
                <div className="file-preview">
                  <img src={imagePreview} alt="Preview" />
                  <div className="remove-file" onClick={(e) => { e.stopPropagation(); clearImage(); }}>
                    <FaTimes />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Uploading...' : 'Upload to Gallery'}
            </button>
          </div>
        </form>
      </div>

      <div className="admin-grid">
        {loading ? (
          <p>Loading gallery...</p>
        ) : items.length > 0 ? items.map(item => (
          <div key={item.id} className="item-card">
            <div className="card-image" style={{ height: '220px' }}>
              <img src={getImageUrl(item.image_url)} alt={item.title} />
            </div>
            <div className="card-body" style={{ padding: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#1b2559' }}>{item.title || 'Untitled Image'}</span>
                <button 
                  onClick={() => handleDelete(item.id)} 
                  className="btn-delete"
                  style={{ background: '#fff5f5', color: '#ff5f5f', border: 'none', padding: '8px', borderRadius: '8px', cursor: 'pointer' }}
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          </div>
        )) : (
          <div style={{ textAlign: 'center', gridColumn: '1/-1', padding: '40px', color: '#a3aed0' }}>
            <p>No images in gallery yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminGallery;
